
export interface PriceListMappingResult {
    productFields: {
        name?: string;
        partNo?: string;
        vender?: string;
        mrp?: number;
        unitPrice?:number;
        brand?: string;
        category?: string;
        purchaseDiscount?: number;
        purchasePrice?: number;
        discountB2C?: number;
        sellingPriceB2C?: number;
        discountB2B?: number;
        sellingPriceB2B?: number;
        quantity?: number;
    };
    descriptionText?: string;
    descriptionFields: Array<{ key: string; value: string }>; 
    sourceId?: string;
    sourceMetadata: Array<{ key: string; value: string }>;
}

export const mapPriceListToProduct = (priceListData: any, sourceId?: string): PriceListMappingResult => {
    const result: PriceListMappingResult = {
        productFields: {},
        descriptionFields: [],
        sourceId,
        sourceMetadata: []
    };

    // Map specific fields to product fields
    if (priceListData.productName || priceListData.name) {
        result.productFields.name = priceListData.productName || priceListData.name;
    }

    if (priceListData.partNumber || priceListData.partNo || priceListData.sku) {
        result.productFields.partNo = priceListData.partNumber || priceListData.partNo || priceListData.sku;
    }

    if (priceListData.supplier || priceListData.vendor || priceListData.dealer || priceListData.vendorName) {
        result.productFields.vender = priceListData.supplier || priceListData.vendor || priceListData.dealer || priceListData.vendorName;
    }

    if (priceListData.mrp || priceListData.price || priceListData.retailPrice) {
        const price = priceListData.mrp || priceListData.price || priceListData.retailPrice;
        result.productFields.mrp = typeof price === 'string' ? parseFloat(price) : price;
    }

    // Map additional product fields
    if (priceListData.brand || priceListData.productBrand) {
        result.productFields.brand = priceListData.brand || priceListData.productBrand;
    }

    if (priceListData.category || priceListData.productCategory) {
        result.productFields.category = priceListData.category || priceListData.productCategory;
    }

    if (priceListData.purchaseDiscount || priceListData.discount) {
        const discount = priceListData.purchaseDiscount || priceListData.discount;
        result.productFields.purchaseDiscount = typeof discount === 'string' ? parseFloat(discount) : discount;
    }

    if (priceListData.purchasePrice || priceListData.costPrice) {
        const purchasePrice = priceListData.purchasePrice || priceListData.costPrice;
        result.productFields.purchasePrice = typeof purchasePrice === 'string' ? parseFloat(purchasePrice) : purchasePrice;
    }

    if (priceListData.sellingPriceB2C || priceListData.sellingPrice) {
        const sellingPrice = priceListData.sellingPriceB2C || priceListData.sellingPrice;
        result.productFields.sellingPriceB2C = typeof sellingPrice === 'string' ? parseFloat(sellingPrice) : sellingPrice;
    }

    if (priceListData.sellingPriceB2B || priceListData.wholesalePrice) {
        const sellingPriceB2B = priceListData.sellingPriceB2B || priceListData.wholesalePrice;
        result.productFields.sellingPriceB2B = typeof sellingPriceB2B === 'string' ? parseFloat(sellingPriceB2B) : sellingPriceB2B;
    }

    if (priceListData.quantity || priceListData.stock) {
        const quantity = priceListData.quantity || priceListData.stock;
        result.productFields.quantity = typeof quantity === 'string' ? parseInt(quantity) : quantity;
    }

    // Extract description text if it exists
    if (priceListData.description && priceListData.description.text) {
        result.descriptionText = priceListData.description.text;
    } else if (priceListData.descriptionText) {
        result.descriptionText = priceListData.descriptionText;
    }

    // Prepare source metadata
    if (priceListData._id) {
        result.sourceMetadata.push({ key: 'priceListItemId', value: priceListData._id });
    }
    if (priceListData.sourceFile) {
        result.sourceMetadata.push({ key: 'sourceFile', value: priceListData.sourceFile });
    }
    if (priceListData.importBatchId) {
        result.sourceMetadata.push({ key: 'importBatch', value: priceListData.importBatchId });
    }

    // FIRST: Add price list's description.jsonFields to result.descriptionFields
    if (priceListData.description && priceListData.description.jsonFields) {
        Object.entries(priceListData.description.jsonFields).forEach(([key, value]) => {
            if (value !== null && value !== undefined) {
                result.descriptionFields.push({
                    key,
                    value: typeof value === 'object' ? JSON.stringify(value) : String(value)
                });
            }
        });
    }

    // SECOND: Add all other unmapped fields to result.descriptionFields
    const excludeFields = new Set([
        // Product mapped fields
        '_id', '__v', 'createdAt', 'updatedAt', 'createdBy', 'updatedBy',
        'productName', 'name', 'partNumber', 'partNo', 'sku',
        'supplier', 'vendor', 'dealer', 'vendorName',
        'mrp', 'price', 'retailPrice',
        'brand', 'productBrand', 'category', 'productCategory',
        'purchaseDiscount', 'discount', 'purchasePrice', 'costPrice',
        'sellingPriceB2C', 'sellingPrice', 'sellingPriceB2B', 'wholesalePrice',
        'quantity', 'stock',
        
        // Source metadata fields
        'importBatchId', 'sourceFile', 'status', 'errors', 'rowNumber',
        
        // Description fields (handled separately)
        'description', 'descriptionText'
    ]);

    // Function to add fields to descriptionFields
    const addToDescriptionFields = (obj: any, prefix = '') => {
        Object.entries(obj).forEach(([key, value]) => {
            const fullKey = prefix ? `${prefix}.${key}` : key;
            
            // Skip excluded fields and null/undefined values
            if (excludeFields.has(key) || value === null || value === undefined) {
                return;
            }

            // Skip if this is the description field (already handled)
            if (key === 'description') {
                // But we still want to process other fields inside description except text
                if (typeof value === 'object') {
                    const descObj = value as any;
                    // Process everything in description except text (already handled) and jsonFields (already handled)
                    Object.entries(descObj).forEach(([descKey, descValue]) => {
                        if (descKey !== 'text' && descKey !== 'jsonFields' && descValue !== null && descValue !== undefined) {
                            result.descriptionFields.push({
                                key: `description.${descKey}`,
                                value: typeof descValue === 'object' ? JSON.stringify(descValue) : String(descValue)
                            });
                        }
                    });
                }
                return;
            }

            // Handle nested objects
            if (typeof value === 'object' && !Array.isArray(value)) {
                addToDescriptionFields(value, fullKey);
            } else {
                // Add simple values
                result.descriptionFields.push({
                    key: fullKey,
                    value: typeof value === 'object' ? JSON.stringify(value) : String(value)
                });
            }
        });
    };

    // Start processing the price list data
    addToDescriptionFields(priceListData);

    // Remove duplicates (in case same field appears multiple times)
    const uniqueFields = new Map();
    result.descriptionFields.forEach(field => {
        uniqueFields.set(field.key, field.value);
    });
    
    // Convert back to array
    result.descriptionFields = Array.from(uniqueFields.entries()).map(([key, value]) => ({
        key,
        value: String(value)
    }));

    return result;
};